import { useRef, useEffect } from 'react';
import { type LiveChatConversation } from '@/mocks/superAdminLiveSupport';
import { streamStatusLabels } from '@/mocks/superAdminLiveSupport';

interface ConversationListProps {
  conversations: LiveChatConversation[];
  activeId: number | null;
  totalUnread: number;
  search: string;
  onSearchChange: (v: string) => void;
  onSelect: (id: number) => void;
}

export default function ConversationList({
  conversations,
  activeId,
  totalUnread,
  search,
  onSearchChange,
  onSelect,
}: ConversationListProps) {
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (activeId && listRef.current) {
      const el = listRef.current.querySelector(`[data-conv-id="${activeId}"]`) as HTMLElement | null;
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
  }, [activeId]);

  return (
    <div className="w-full sm:w-[300px] lg:w-[320px] shrink-0 bg-background-50 border border-background-200 rounded-2xl overflow-hidden flex flex-col">
      {/* Header */}
      <div className="px-4 py-3 border-b border-background-200 space-y-2.5">
        <div className="flex items-center justify-between">
          <p className="text-foreground-800 text-xs font-bold">Conversaciones</p>
          <span className="text-foreground-400 text-[10px]">{totalUnread} sin leer</span>
        </div>
        <div className="relative">
          <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-foreground-400 text-[11px]"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Buscar radio..."
            className="w-full pl-7 pr-3 py-1.5 bg-background-100 border border-background-200 rounded-lg text-[10px] text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors"
          />
        </div>
      </div>

      {/* List */}
      <div ref={listRef} className="flex-1 overflow-y-auto">
        {conversations.length === 0 ? (
          <div className="px-4 py-10 text-center">
            <i className="ri-search-line text-foreground-300 text-xl mb-2 block"></i>
            <p className="text-foreground-400 text-[10px]">Sin resultados</p>
          </div>
        ) : (
          conversations.map((conv) => {
            const sStat = streamStatusLabels[conv.streamStatus];
            return (
              <button
                key={conv.id}
                data-conv-id={conv.id}
                onClick={() => onSelect(conv.id)}
                className={`w-full text-left px-4 py-3 border-b border-background-100 transition-colors cursor-pointer ${
                  activeId === conv.id ? 'bg-background-100' : 'hover:bg-background-50'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  {/* Avatar */}
                  <div className="relative shrink-0">
                    <div className="w-9 h-9 rounded-full bg-foreground-950 flex items-center justify-center">
                      <span className="text-background-50 text-[9px] font-bold">
                        {conv.radioName.slice(0, 2).toUpperCase()}
                      </span>
                    </div>
                    {conv.online && (
                      <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-400 border-2 border-background-50 rounded-full"></div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <p className="text-foreground-800 text-xs font-semibold truncate">{conv.radioName}</p>
                        {conv.transferredFromTicket && (
                          <span className="shrink-0 px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-600 text-[8px] font-bold flex items-center gap-0.5">
                            <i className="ri-ticket-2-line text-[7px]"></i>
                            {conv.transferredFromTicket}
                          </span>
                        )}
                      </div>
                      {conv.unread > 0 && (
                        <span className="w-5 h-5 rounded-full bg-emerald-500 text-white text-[9px] font-bold flex items-center justify-center shrink-0 ml-1">
                          {conv.unread}
                        </span>
                      )}
                    </div>
                    <p className="text-foreground-400 text-[10px] truncate mt-0.5">{conv.lastMessage}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-foreground-300 text-[9px]">{conv.lastMessageAt}</p>
                      <span className={`w-1.5 h-1.5 rounded-full ${sStat.dot}`} title={sStat.label}></span>
                      <span className="text-foreground-300 text-[9px] truncate">{conv.planName}</span>
                    </div>
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}